import * as React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu';
import {Drawer, DrawerContent, DrawerTitle} from '../drawer';
import {useScreenResize} from '../hooks';
import {DEVICE} from '../lib/device';
import {cn} from '../lib/cn';
import {isMobileModalModeAvailable} from '../lib/runtimeSettings';

type DropdownSide = 'top' | 'right' | 'bottom' | 'left';
type DropdownAlign = 'start' | 'center' | 'end';

type ResponsiveDropdownOption = {
  id: string | number;
  label: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  hidden?: boolean;
  disabled?: boolean;
};

type ResponsiveDropdownProps = {
  trigger: React.ReactElement | ((isOpen: boolean) => React.ReactElement);
  options?: ResponsiveDropdownOption[];
  children?: React.ReactNode;
  side?: DropdownSide;
  align?: DropdownAlign;
  disabled?: boolean;
  title?: React.ReactNode;
  className?: string;
  dropdownClassName?: string;
  drawerClassName?: string;
};

function ResponsiveDropdown({
  trigger,
  options,
  children,
  side,
  align,
  disabled,
  title,
  className,
  dropdownClassName,
  drawerClassName,
}: Readonly<ResponsiveDropdownProps>) {
  const {isMatch: isMobile} = useScreenResize(DEVICE.mobileXL);
  const isMobileMode = isMobile && isMobileModalModeAvailable();
  const [open, setOpen] = React.useState(false);

  const visibleOptions = options?.filter(option => !option.hidden) ?? [];

  const renderTrigger = (isOpen: boolean) =>
    typeof trigger === 'function' ? trigger(isOpen) : trigger;

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) {
      return;
    }
    setOpen(nextOpen);
  };

  const handleOptionClick =
    (option: ResponsiveDropdownOption) => (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled || option.disabled) {
        return;
      }
      option.onClick?.(event);
      setOpen(false);
    };

  if (isMobileMode) {
    return (
      <>
        <div
          className="responsive-dropdown__mobile-trigger"
          onClick={() => handleOpenChange(true)}
        >
          {renderTrigger(open)}
        </div>
        <Drawer open={open} onOpenChange={handleOpenChange}>
          <DrawerContent
            onClose={() => setOpen(false)}
            lockScroll={false}
            className={cn('px-0 pb-2', className, drawerClassName)}
          >
            {title ? <DrawerTitle className="sr-only">{title}</DrawerTitle> : null}
            <div className="flex flex-col">
              {visibleOptions.map(option => (
                <div
                  key={option.id}
                  role="button"
                  tabIndex={0}
                  aria-disabled={option.disabled}
                  onClick={handleOptionClick(option)}
                  className={cn(
                    'cursor-pointer px-5 py-3 text-base font-medium text-[var(--chekin-color-brand-navy)] outline-none hover:bg-gray-50',
                    option.disabled && 'pointer-events-none opacity-50',
                  )}
                >
                  {option.label}
                </div>
              ))}
              {children}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        {renderTrigger(open)}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={side}
        align={align}
        className={cn('min-w-[295px]', className, dropdownClassName)}
      >
        {visibleOptions.map(option => (
          <DropdownMenuItem
            key={option.id}
            disabled={option.disabled}
            onClick={handleOptionClick(option)}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export {ResponsiveDropdown};
export type {ResponsiveDropdownProps, ResponsiveDropdownOption};
